import { type Node, Position } from '@xyflow/react';
import * as Api from '@ViewModels';
import { type BaseNodeData, type NodeCategory, NODE_TYPES } from '../types';
import type { AutomationStepNodeData, BehaviorCaseNodeData, SegmentNodeData } from '../types/stepNodes';
import { BRANCH_COLORS, DEFAULT_BRANCH_COLOR } from '../edges/branchEdgeStyles';
import { nodeTypeToStepType } from './stepTypeMapping';
import {
	NODE_WIDTH,
	SEGMENT_NODE,
	BEHAVIOR_CASE_NODE,
	STEP_NODE_EXPANDED_MIN_HEIGHT,
	TRIGGER_NODE,
} from './layoutConstants';
import { extractPrimaryAndAdditionalCriteria } from './segmentCriteria';
import { getBehaviorCaseLabel } from './behaviorCaseLabels';

const TRIGGER_AUTOMATION_STEP_TYPE = 'TriggerAutomation' as Api.AutomationStepType;

let nodeIdCounter = 0;

export function generateNodeId(type: string): string {
	return `${type}-${Date.now()}-${nodeIdCounter++}`;
}

function createStableBranchEntryNodeId(prefix: string, parentSwitchNodeId: string, caseId: string): string {
	const normalizedCaseId = encodeURIComponent(caseId).replace(/%/g, '_');
	return `${prefix}-${parentSwitchNodeId}-${normalizedCaseId}`;
}

function createNode(
	type: string,
	position: { x: number; y: number },
	category: NodeCategory,
	label: string,
	summary?: string
): Node<BaseNodeData> {
	return {
		id: generateNodeId(type),
		type,
		position,
		data: {
			label,
			summary,
			category,
			metadata: {
				category,
				createdAt: new Date().toISOString(),
			},
		},
	};
}

export function createTriggerNode(position: { x: number; y: number }): Node<BaseNodeData> {
	const node = createNode(
		NODE_TYPES.TRIGGER,
		position,
		'trigger',
		'Trigger: New Client',
		'Starts when a new client is added'
	);
	return {
		...node,
		width: TRIGGER_NODE.WIDTH,
		height: TRIGGER_NODE.HEIGHT,
		data: {
			...node.data,
			outputHandles: ['default'],
		},
	};
}

/**
 * Configuration for creating step nodes. Centralizes metadata and validation logic.
 */
interface StepTypeConfig {
	nodeType: string;
	apiStepType: Api.AutomationStepType;
	category: NodeCategory;
	label: string;
	summary: string;
	isValid: (step: Api.IAutomationStep | undefined) => boolean;
	hasContent?: (step: Api.IAutomationStep | undefined) => boolean;
	outputHandles?: string[];
}

/**
 * Centralized configuration for all step types.
 * Eliminates repetitive create*StepNode functions.
 */
const STEP_TYPE_CONFIGS: Record<Api.AutomationStepType, StepTypeConfig> = {
	[Api.AutomationStepType.Email]: {
		nodeType: NODE_TYPES.EMAIL_STEP,
		apiStepType: Api.AutomationStepType.Email,
		category: 'email',
		label: 'Send Email',
		summary: 'Delivers an email to contacts',
		isValid: step => !!(step?.name && (step as Api.IEmailAutomationStep)?.content),
		hasContent: step => !!(step as Api.IEmailAutomationStep)?.content,
	},
	[Api.AutomationStepType.Texting]: {
		nodeType: NODE_TYPES.TEXTING_STEP,
		apiStepType: Api.AutomationStepType.Texting,
		category: 'email',
		label: 'Send Text Message',
		summary: 'Sends an SMS to contacts',
		isValid: step => !!(step?.name && (step as Api.ITextingAutomationStep)?.content),
		hasContent: step => !!(step as Api.ITextingAutomationStep)?.content,
	},
	[Api.AutomationStepType.ActionItem]: {
		nodeType: NODE_TYPES.ACTION_ITEM_STEP,
		apiStepType: Api.AutomationStepType.ActionItem,
		category: 'task',
		label: 'Create Action Item',
		summary: 'Creates a follow-up task',
		isValid: step => !!step?.name,
	},
	[Api.AutomationStepType.AddTag]: {
		nodeType: NODE_TYPES.ADD_TAG_STEP,
		apiStepType: Api.AutomationStepType.AddTag,
		category: 'action',
		label: 'Add Tag',
		summary: 'Tags contacts for segmentation',
		isValid: step => !!(step as Api.ITagAutomationStep)?.tagName,
	},
	[Api.AutomationStepType.RemoveTag]: {
		nodeType: NODE_TYPES.REMOVE_TAG_STEP,
		apiStepType: Api.AutomationStepType.RemoveTag,
		category: 'action',
		label: 'Remove Tag',
		summary: 'Removes a tag from contacts',
		isValid: step => !!(step as Api.IRemoveTagAutomationStep)?.tagName,
	},
	[Api.AutomationStepType.HandwrittenCard]: {
		nodeType: NODE_TYPES.HANDWRITTEN_CARD_STEP,
		apiStepType: Api.AutomationStepType.HandwrittenCard,
		category: 'email',
		label: 'Send Handwritten Card',
		summary: 'Mails a handwritten card',
		isValid: step => !!(step as Api.HandwrittenCardAutomationStep)?.templateReference,
	},
	[Api.AutomationStepType.NoAction]: {
		nodeType: NODE_TYPES.NO_ACTION_STEP,
		apiStepType: Api.AutomationStepType.NoAction,
		category: 'action',
		label: 'Wait / Delay',
		summary: 'Pauses before the next step',
		isValid: () => true,
	},
	[Api.AutomationStepType.Opportunity]: {
		nodeType: NODE_TYPES.OPPORTUNITY_STEP,
		apiStepType: Api.AutomationStepType.Opportunity,
		category: 'task',
		label: 'Manage Opportunity',
		summary: 'Creates or updates an opportunity',
		isValid: step => !!step?.name,
	},
	[Api.AutomationStepType.HtmlNewsletter]: {
		nodeType: NODE_TYPES.HTML_NEWSLETTER_STEP,
		apiStepType: Api.AutomationStepType.HtmlNewsletter,
		category: 'email',
		label: 'Send Newsletter',
		summary: 'Delivers an HTML newsletter',
		isValid: step => !!(step?.name && (step as Api.IHtmlNewsletterAutomationStep)?.content),
		hasContent: step => !!(step as Api.IHtmlNewsletterAutomationStep)?.content,
	},
	[Api.AutomationStepType.Switch]: {
		nodeType: NODE_TYPES.SWITCH_STEP,
		apiStepType: Api.AutomationStepType.Switch,
		category: 'action',
		label: 'Conditional Branch',
		summary: 'Routes contacts based on conditions',
		isValid: step => ((step as Api.ISwitchAutomationStep)?.cases?.length || 0) > 0,
	},
};

const TRIGGER_AUTOMATION_CONFIG: StepTypeConfig = {
	nodeType: NODE_TYPES.TRIGGER_AUTOMATION_STEP,
	apiStepType: TRIGGER_AUTOMATION_STEP_TYPE,
	category: 'action',
	label: 'Start Automation',
	summary: 'Triggers another automation for the same contact',
	isValid: step => {
		const typedStep = step as { targetAutomationTemplateId?: string } | undefined;
		return !!typedStep?.targetAutomationTemplateId;
	},
	outputHandles: [],
};

/**
 * Creates a step node from config. Used by createStepNodeByType and individual create functions.
 */
function createStepNodeFromConfig(
	config: StepTypeConfig,
	position: { x: number; y: number },
	automationStep?: Api.IAutomationStep,
	automationTemplate?: Api.IAutomationTemplate
): Node<AutomationStepNodeData> {
	const nodeId = generateNodeId(config.nodeType);
	const outputHandles = config.outputHandles ?? ['default'];
	return {
		id: nodeId,
		type: config.nodeType,
		position,
		width: NODE_WIDTH,
		height: STEP_NODE_EXPANDED_MIN_HEIGHT,
		data: {
			label: config.label,
			summary: config.summary,
			category: config.category,
			nodeId,
			stepId: automationStep?.id,
			stepType: config.apiStepType,
			automationStep,
			automationTemplate,
			isExpanded: true,
			isCompact: false,
			estimatedHeight: STEP_NODE_EXPANDED_MIN_HEIGHT,
			isValid: config.isValid(automationStep),
			validationErrors: [],
			targetPosition: Position.Top,
			sourcePosition: Position.Bottom,
			stepData: {
				name: automationStep?.name,
				schedule: automationStep?.schedule,
				hasContent: config.hasContent ? config.hasContent(automationStep) : false,
			},
			outputHandles,
		},
	};
}

export function createSwitchStepNode(
	position: { x: number; y: number },
	automationStep?: Api.ISwitchAutomationStep,
	automationTemplate?: Api.IAutomationTemplate
): Node<AutomationStepNodeData> {
	const switchStep = automationStep as Api.ISwitchAutomationStep | undefined;
	const cases = switchStep?.cases || [];
	const nodeId = generateNodeId(NODE_TYPES.SWITCH_STEP);

	// Generate output handles for each case
	const outputHandles = cases.length > 0 ? cases.map((c, i) => (c.isDefault ? 'default' : `case-${i}`)) : ['default'];

	return {
		id: nodeId,
		type: NODE_TYPES.SWITCH_STEP,
		position,
		width: NODE_WIDTH,
		height: STEP_NODE_EXPANDED_MIN_HEIGHT,
		data: {
			label: 'Conditional Branch',
			summary: 'Routes contacts based on conditions',
			category: 'action',
			nodeId,
			stepId: automationStep?.id,
			stepType: Api.AutomationStepType.Switch,
			automationStep,
			automationTemplate,
			isExpanded: true, // Switch nodes are expanded by default to show branches
			isCompact: false,
			estimatedHeight: STEP_NODE_EXPANDED_MIN_HEIGHT,
			isValid: cases.length > 0,
			validationErrors: cases.length === 0 ? ['At least one branch is required'] : [],
			targetPosition: Position.Top,
			sourcePosition: Position.Bottom,
			stepData: {
				name: automationStep?.name || 'Conditional Branch',
				schedule: automationStep?.schedule,
				hasContent: cases.length > 0,
			},
			outputHandles,
		},
	};
}

/**
 * Creates a segment node for conditional branch entry points
 *
 * Segments contain filter criteria configuration that determines which contacts
 * flow through each branch. Action steps connect BELOW segments via edges.
 *
 * @param caseStatement - The case statement from the switch step
 * @param segmentIndex - Zero-based index of this segment
 * @param parentSwitchNodeId - ID of the parent switch node
 * @param position - Position in the flow canvas
 */
export function createSegmentNode(
	caseStatement: Api.IAutomationStepCaseStatement,
	segmentIndex: number,
	parentSwitchNodeId: string,
	position: { x: number; y: number },
	childTemplateId?: string
): Node<SegmentNodeData> {
	const caseId = caseStatement.id || `case-${segmentIndex}`;
	const nodeId = createStableBranchEntryNodeId(NODE_TYPES.SEGMENT, parentSwitchNodeId, caseId);
	const isDefault = caseStatement.isDefault || false;
	const caseColor = isDefault ? DEFAULT_BRANCH_COLOR : BRANCH_COLORS[segmentIndex % BRANCH_COLORS.length];

	// Extract primary and additional criteria from filter
	const { primaryCriteria, additionalCriteria } = extractCriteriaFromFilter(caseStatement.filter, isDefault);

	return {
		id: nodeId,
		type: NODE_TYPES.SEGMENT,
		position,
		width: SEGMENT_NODE.WIDTH,
		height: SEGMENT_NODE.MIN_HEIGHT,
		data: {
			label: `Segment #${segmentIndex + 1}`,
			category: 'action',
			segmentIndex,
			isDefault,
			parentSwitchNodeId,
			caseId,
			segmentName: caseStatement.name || undefined,
			primaryCriteria,
			additionalCriteria,
			caseColor,
			isExpanded: true,
			targetPosition: Position.Top,
			sourcePosition: Position.Bottom,
			childTemplateId,
		},
	};
}

export function createBehaviorCaseNode(
	caseStatement: Api.IAutomationStepCaseStatement & {
		_type?: string;
		caseType?: string;
		engagementType?: string;
		waitTimeoutDays?: number;
		isTimeout?: boolean;
		sourceStepName?: string;
	},
	caseIndex: number,
	parentSwitchNodeId: string,
	position: { x: number; y: number },
	childTemplateId?: string
): Node<BehaviorCaseNodeData> {
	const caseId = caseStatement.id || `behavior-case-${caseIndex}`;
	const nodeId = createStableBranchEntryNodeId(NODE_TYPES.BEHAVIOR_CASE, parentSwitchNodeId, caseId);
	const caseType = (caseStatement.caseType || caseStatement._type || '') as string;
	const isTimeoutCase =
		caseStatement.isDefault ||
		caseStatement.isTimeout ||
		caseType === 'TimeoutCaseStatement' ||
		caseType === 'DefaultCaseStatement';
	const caseColor = isTimeoutCase ? DEFAULT_BRANCH_COLOR : BRANCH_COLORS[caseIndex % BRANCH_COLORS.length];
	const caseLabel = getBehaviorCaseLabel(caseStatement);

	return {
		id: nodeId,
		type: NODE_TYPES.BEHAVIOR_CASE,
		position,
		width: BEHAVIOR_CASE_NODE.WIDTH,
		height: BEHAVIOR_CASE_NODE.HEIGHT,
		data: {
			label: caseLabel,
			category: 'action',
			parentSwitchNodeId,
			caseId,
			caseIndex,
			isDefault: !!isTimeoutCase,
			caseColor,
			caseType: caseType || undefined,
			engagementType: caseStatement.engagementType,
			waitTimeoutDays: caseStatement.waitTimeoutDays,
			caseName: caseLabel,
			isTimeout: !!isTimeoutCase,
			isExpanded: true,
			targetPosition: Position.Top,
			sourcePosition: Position.Bottom,
			childTemplateId,
			sourceStepName: caseStatement.sourceStepName,
		},
	};
}

/**
 * Extracts primary and additional criteria from a filter criteria object
 *
 * This utility parses the filter criteria structure to populate the segment's
 * "IF" dropdown and additional filters. The primary criteria is the main
 * filter condition, and additional criteria are any nested AND/OR/NOT filters.
 *
 * @param filter - The filter criteria from the case statement
 * @param isDefault - Whether this is the default segment
 */
function extractCriteriaFromFilter(
	filter?: Api.IContactFilterCriteria,
	isDefault = false
): {
	primaryCriteria?: Api.IContactFilterCriteria;
	additionalCriteria?: Api.IContactFilterCriteria[];
} {
	return extractPrimaryAndAdditionalCriteria(filter, isDefault);
}

/**
 * Factory function to create a step node based on step type.
 * Uses config-driven approach for standard step types, with special handling for Switch.
 */
export function createStepNodeByType(
	stepType: Api.AutomationStepType | string,
	position: { x: number; y: number },
	automationStep?: Api.IAutomationStep,
	automationTemplate?: Api.IAutomationTemplate
): Node<AutomationStepNodeData> | null {
	// Resolve to API step type (handles both Api.AutomationStepType and NODE_TYPES strings)
	const apiStepType =
		typeof stepType === 'string' ? (nodeTypeToStepType(stepType) ?? (stepType as Api.AutomationStepType)) : stepType;

	// Switch has custom logic for output handles, use dedicated function
	if (apiStepType === Api.AutomationStepType.Switch) {
		return createSwitchStepNode(position, automationStep as Api.ISwitchAutomationStep, automationTemplate);
	}

	if (apiStepType === TRIGGER_AUTOMATION_STEP_TYPE) {
		return createStepNodeFromConfig(TRIGGER_AUTOMATION_CONFIG, position, automationStep, automationTemplate);
	}

	// Use config-driven approach for all other step types
	const config = STEP_TYPE_CONFIGS[apiStepType as Api.AutomationStepType];
	if (!config) {
		return null;
	}

	return createStepNodeFromConfig(config, position, automationStep, automationTemplate);
}

/**
 * Creates a blank segment node for inline creation when user clicks "Add Path" button.
 *
 * The blank segment has empty tag arrays and default timing settings.
 * It's inserted before the default segment, which always stays rightmost.
 *
 * @param parentSwitchNodeId - ID of the parent switch node
 * @param segmentIndex - Zero-based index for this segment (inserted before default)
 * @param position - Position in the flow canvas
 * @param totalExistingSegments - Total number of existing segments (to determine color)
 */
export function createBlankSegmentNode(
	parentSwitchNodeId: string,
	segmentIndex: number,
	position: { x: number; y: number },
	totalExistingSegments = 0
): Node<SegmentNodeData> {
	const nodeId = generateNodeId(NODE_TYPES.SEGMENT);
	const caseColor = BRANCH_COLORS[totalExistingSegments % BRANCH_COLORS.length];

	return {
		id: nodeId,
		type: NODE_TYPES.SEGMENT,
		position,
		width: SEGMENT_NODE.WIDTH,
		height: SEGMENT_NODE.MIN_HEIGHT,
		data: {
			label: `Segment #${segmentIndex + 1}`,
			category: 'action',
			segmentIndex,
			isDefault: false,
			parentSwitchNodeId,
			caseId: `case-${nodeId}`,
			primaryCriteria: undefined,
			additionalCriteria: undefined,
			caseColor,
			isExpanded: true,
			targetPosition: Position.Top,
			sourcePosition: Position.Bottom,
		},
	};
}
